import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.55;

  /** @type {boolean} */
  #hasAnimatedItems = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener('click', this.#handleUpsellCardClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener('click', this.#handleUpsellCardClick);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      requestAnimationFrame(() => this.#animateItemsOnFirstOpen());
    }
  };

  #handleDialogOpen = () => {
    this.#updateStickyState();
    this.#animateItemsOnFirstOpen();
  };

  /**
   * Submit cart drawer upsells when the card surface is clicked, while leaving Swiper drags
   * and nested controls to their native handlers.
   *
   * @param {MouseEvent} event
   */
  #handleUpsellCardClick = (event) => {
    if (event.defaultPrevented) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    if (this.#isInteractiveUpsellTarget(target)) return;

    const card = target.closest('[data-cart-drawer-upsell-card]');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;
    if (!this.#shouldHandleUpsellCardClick(card)) return;

    const form = card.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const submitButton = form.querySelector('button[type="submit"], button:not([type])');
    if (submitButton instanceof HTMLButtonElement && submitButton.disabled) return;

    if (submitButton instanceof HTMLElement) {
      form.requestSubmit(submitButton);
      return;
    }

    form.requestSubmit();
  };

  /**
   * @param {Element} target
   * @returns {boolean}
   */
  #isInteractiveUpsellTarget(target) {
    return Boolean(
      target.closest(
        [
          'a[href]',
          'button',
          'input',
          'select',
          'textarea',
          'summary',
          '[role="button"]',
          '[data-swiper-pagination]',
        ].join(',')
      )
    );
  }

  /**
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  #shouldHandleUpsellCardClick(card) {
    const swiperComponent = card.closest('[data-swiper-component]');
    const swiper = swiperComponent?.__swiperInstances?.main;

    if (!swiper) return true;
    if (swiper.allowClick === false) return false;

    return true;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #animateItemsOnFirstOpen() {
    if (this.#hasAnimatedItems) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || dialog.classList.contains('cart-drawer--empty')) return;

    const items = this.#readAnimationItems(dialog);
    const lastItem = items[items.length - 1];
    if (!(lastItem instanceof HTMLElement)) return;

    items.forEach((item, index) => {
      item.dataset.cartDrawerAnimateItem = '';
      item.style.setProperty(
        '--cart-drawer-item-animation-index',
        Math.min(index, 18).toString()
      );
    });

    this.#hasAnimatedItems = true;
    dialog.dataset.state = 'cart-drawer-animate-in';

    const clearAnimationState = () => {
      if (dialog.dataset.state === 'cart-drawer-animate-in') {
        delete dialog.dataset.state;
      }

      items.forEach((item) => {
        delete item.dataset.cartDrawerAnimateItem;
        item.style.removeProperty('--cart-drawer-item-animation-index');
      });
    };

    lastItem.addEventListener('animationend', clearAnimationState, { once: true });
    window.setTimeout(clearAnimationState, 900);
  }

  /**
   * Reads visible drawer modules in rendered DOM order so optional cart panels join the same cascade.
   * @param {HTMLDialogElement} dialog
   * @returns {HTMLElement[]}
   */
  #readAnimationItems(dialog) {
    const candidates = dialog.querySelectorAll(
      [
        '.cart-drawer__sticky-header .cart-drawer__heading',
        '.cart-drawer__sticky-header > .cart-threshold-progress',
        '.cart-drawer__items .cart-items__table-row:not([aria-hidden="true"])',
        '.cart-drawer__items .cart-drawer-gifts',
        '.cart-drawer__summary > .cart__summary-totals',
        '.cart-drawer__summary .cart-actions > :not(script):not(style)',
        '.cart-drawer__summary > .cart__ctas',
      ].join(',')
    );

    return Array.from(candidates).filter((candidate) => (
      candidate instanceof HTMLElement
        && candidate.offsetParent !== null
        && !candidate.hidden
    ));
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
